/**
 * Natural-language parsing of the quick-add title field.
 *
 * The goal is the Todoist / Things "type-it-and-go" feel: a user
 * writes `Buy milk tomorrow at 3pm every day` and the form
 * pre-fills its date, time, and recurrence pills from the phrase
 * — then strips the recognised tokens from the saved title.
 *
 * Two parsers cooperate:
 *  - `chrono-node` for dates and times. Handles `tomorrow`,
 *    `next Friday`, `in 3 days`, `on June 12`, `at 3pm`, etc. with
 *    English-language nuance we don't want to reinvent.
 *  - A small regex matcher for recurrence words. Chrono doesn't
 *    parse cadence ("every day" / "weekly" / "every Monday"), so
 *    we cover the four standard cadences the rest of the app
 *    supports.
 *
 * Both parsers return their match ranges; we strip the union from
 * the title so phrases that span both matchers (`every Monday at 7`)
 * fall out cleanly without leftover artefacts.
 */

import * as chrono from 'chrono-node';

export type ParsedRecurrence = 'daily' | 'weekly' | 'monthly' | 'yearly';
export type ParsedPriority = 'low' | 'medium' | 'high';

export interface ParseResult {
  /** Title with all recognised tokens removed, whitespace
   *  normalised. Empty if the entire input was a date/recurrence/
   *  priority phrase. Category keywords are NOT stripped — they're
   *  integral to the task title (`Pay credit card bill` keeps its
   *  words even though it nudges the category to Finances). */
  title: string;
  /** ISO `YYYY-MM-DD` if chrono recognised a date phrase. */
  dueDate?: string;
  /** `HH:MM` (24-hour) if chrono recognised an explicit time.
   *  Undefined when only a date was given. */
  dueTime?: string;
  /** Recurrence cadence if a phrase like `every day` matched. */
  recurrence?: ParsedRecurrence;
  /** Priority bumped to Low / High by an urgency keyword
   *  (`urgent`, `!!`, `someday`, …). Undefined if no marker found —
   *  the form's default Medium continues to apply. */
  priority?: ParsedPriority;
  /** Life-area slug (`finances`, `health`, …) suggested by
   *  keyword matches in the title. Undefined if no keyword fired
   *  with enough confidence to pick a side. */
  categorySlug?: string;
}

// Recurrence patterns ordered specific → generic so a more
// targeted match (e.g. `every Monday` → weekly) wins over a
// looser one. Day-of-week phrases imply weekly cadence anchored
// on that day; chrono's date match anchors the dueDate, so we
// just need to flag the cadence here.
const RECURRENCE_PATTERNS: { regex: RegExp; mode: ParsedRecurrence }[] = [
  { regex: /\b(every\s+day|daily)\b/iu, mode: 'daily' },
  {
    regex:
      /\b(every\s+(?:monday|tuesday|wednesday|thursday|friday|saturday|sunday|week)|weekly)\b/iu,
    mode: 'weekly',
  },
  { regex: /\b(every\s+month|monthly)\b/iu, mode: 'monthly' },
  { regex: /\b(every\s+year|yearly|annually)\b/iu, mode: 'yearly' },
];

// Priority markers — stripped from the title once recognised
// since they're meta, not part of what the task is. `!!` is a
// short tactile shorthand used by Todoist users; `!low` / `!high`
// are the explicit forms. Word forms (`urgent`, `critical`, …)
// cover natural English. The order doesn't matter — the first
// matching variant wins via Array.find.
const PRIORITY_PATTERNS: { regex: RegExp; priority: ParsedPriority }[] = [
  {
    regex: /\b(urgent|critical|asap|important|high\s+priority)\b/iu,
    priority: 'high',
  },
  { regex: /!{2,}|!high|!urgent/iu, priority: 'high' },
  { regex: /\b(whenever|someday|no\s+rush|low\s+priority)\b/iu, priority: 'low' },
  { regex: /!low/iu, priority: 'low' },
];

// Keyword → life-area slug. Sourced from the seed migration's
// six task-shaped areas (Archive is family-visibility and skipped
// here). The lists are intentionally short — common patterns,
// not a thesaurus. False positives are expected and survivable;
// the user-touch override on the category pill is one tap.
//
// Ordering inside each list doesn't matter for matching, but the
// overall slug order does: when two areas tie on hit count, the
// earlier slug in this map wins. We put the more specific areas
// (Finances, Health) before broader ones so a hit there beats a
// coincidental Personal/Household keyword.
const CATEGORY_KEYWORDS: { slug: string; keywords: readonly string[] }[] = [
  {
    slug: 'finances',
    keywords: [
      'pay',
      'paid',
      'bill',
      'bills',
      'loan',
      'mortgage',
      'rent',
      'tax',
      'taxes',
      'insurance',
      'credit card',
      'bank',
      'invoice',
      'salary',
      'savings',
      'pension',
      'transfer',
    ],
  },
  {
    slug: 'health',
    keywords: [
      'doctor',
      'dentist',
      'prescription',
      'medicine',
      'vaccine',
      'appointment',
      'blood test',
      'check-up',
      'checkup',
      'pharmacy',
      'physio',
      'therapy',
      'optician',
    ],
  },
  {
    slug: 'travel',
    keywords: [
      'book',
      'flight',
      'hotel',
      'airbnb',
      'visa',
      'packing',
      'trip',
      'vacation',
      'holiday',
      'passport stamp',
      'boarding',
    ],
  },
  {
    slug: 'documents',
    keywords: [
      'renew',
      'expire',
      'expires',
      'expiry',
      'certificate',
      'passport',
      'license',
      'licence',
      'permit',
      'id card',
      'registration',
      'contract',
    ],
  },
  {
    slug: 'household',
    keywords: [
      'clean',
      'vacuum',
      'laundry',
      'dishes',
      'plumber',
      'electrician',
      'repair',
      'maintenance',
      'garden',
      'fix',
      'grocery',
      'groceries',
      'shopping',
    ],
  },
];

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function isoDateFromDate(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/**
 * Pick the life-area slug that has the most keyword hits in the
 * lower-cased title. Returns undefined when no keyword fires, so
 * the form's default (Personal) keeps applying. Ties are broken
 * by the order of `CATEGORY_KEYWORDS` — see comment above for
 * the rationale.
 */
function suggestCategorySlug(text: string): string | undefined {
  const lower = text.toLowerCase();
  let best: { slug: string; score: number } | undefined;
  for (const { slug, keywords } of CATEGORY_KEYWORDS) {
    let score = 0;
    for (const keyword of keywords) {
      // Word-boundary check via simple includes plus a surrounding
      // character test. Avoids matching `pay` inside `payment` —
      // we want intentional words, not substrings.
      const idx = lower.indexOf(keyword);
      if (idx < 0) continue;
      const before = idx === 0 ? ' ' : lower[idx - 1]!;
      const after = lower[idx + keyword.length] ?? ' ';
      if (/\w/u.test(before) || /\w/u.test(after)) continue;
      score += 1;
    }
    if (score > 0 && (!best || score > best.score)) {
      best = { slug, score };
    }
  }
  return best?.slug;
}

/**
 * Parse the quick-add title and pull out date/time/recurrence/
 * priority tokens, plus a suggested life-area. Always returns a
 * `ParseResult` — when nothing matches, `title` is the trimmed
 * input and the other fields are undefined.
 */
export function parseTitle(text: string, refDate: Date = new Date()): ParseResult {
  const ranges: [number, number][] = [];
  let dueDate: string | undefined;
  let dueTime: string | undefined;
  let recurrence: ParsedRecurrence | undefined;
  let priority: ParsedPriority | undefined;

  // Chrono date/time pass. `forwardDate: true` biases `Friday`
  // / `the 12th` / etc. toward the next occurrence rather than the
  // most recent past one — matches what a user typing into a
  // task quick-add naturally means.
  const chronoResults = chrono.parse(text, refDate, { forwardDate: true });
  for (const r of chronoResults) {
    const start = r.start.date();
    if (Number.isNaN(start.getTime())) continue;
    // First chrono match wins for the dueDate — a user typing two
    // dates into one task title is unusual; we take the first to
    // avoid surprises.
    if (!dueDate) dueDate = isoDateFromDate(start);
    // Only treat the hour as a real time if chrono is confident
    // the user wrote it. `isCertain('hour')` is true for explicit
    // strings like `at 3pm` / `15:00` / `noon`; it's false when
    // chrono defaulted to midnight because only a date was given.
    if (!dueTime && r.start.isCertain('hour')) {
      dueTime = `${pad2(start.getHours())}:${pad2(start.getMinutes())}`;
    }
    ranges.push([r.index, r.index + r.text.length]);
  }

  // Recurrence pass.
  for (const { regex, mode } of RECURRENCE_PATTERNS) {
    const match = regex.exec(text);
    if (!match || match.index === undefined) continue;
    if (!recurrence) recurrence = mode;
    ranges.push([match.index, match.index + match[0].length]);
  }

  // Priority pass. Same strip-the-tokens treatment as recurrence —
  // the marker is meta, not part of what the task is.
  for (const { regex, priority: p } of PRIORITY_PATTERNS) {
    const match = regex.exec(text);
    if (!match || match.index === undefined) continue;
    if (!priority) priority = p;
    ranges.push([match.index, match.index + match[0].length]);
  }

  // Strip the union of matched ranges. Sort + sweep handles
  // overlapping matches (e.g. chrono's `Monday at 7` overlapping
  // the recurrence's `every Monday`) without leaving stray words.
  ranges.sort((a, b) => a[0] - b[0]);
  const kept: string[] = [];
  let cursor = 0;
  for (const [start, end] of ranges) {
    if (start >= cursor) {
      kept.push(text.slice(cursor, start));
      cursor = end;
    } else if (end > cursor) {
      cursor = end;
    }
  }
  kept.push(text.slice(cursor));
  const stripped = kept
    .join('')
    // Collapse runs of whitespace caused by mid-string strips.
    .replace(/\s+/gu, ' ')
    // Tidy up trailing punctuation that the stripped tokens may
    // have left behind, e.g. `Buy milk .` from `Buy milk tomorrow.`.
    .replace(/\s+([.,;:!?])/gu, '$1')
    // Trailing-connector cleanup. Chrono matches the noun part of
    // a date phrase but not always the preposition that introduced
    // it, so `Buy groceries for the weekend` would otherwise leave
    // a `for the` tail after the weekend got stripped. Strip a
    // trailing preposition (with optional `the` and any punctuation
    // that drifted to the end). Mid-string occurrences are
    // unaffected because the regex anchors with `$`.
    .replace(
      /\s+(for|on|at|by|in|to|of|from|until|till)(?:\s+the)?[\s.,;:!?]*$/iu,
      '',
    )
    .trim();

  // Category suggestion runs against the *stripped* title — the
  // user's actual task words — so a meta token like `urgent`
  // doesn't accidentally weight the score.
  const categorySlug = suggestCategorySlug(stripped);

  return {
    title: stripped,
    ...(dueDate ? { dueDate } : {}),
    ...(dueTime ? { dueTime } : {}),
    ...(recurrence ? { recurrence } : {}),
    ...(priority ? { priority } : {}),
    ...(categorySlug ? { categorySlug } : {}),
  };
}
